import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, Send, Sparkles } from "lucide-react";

import { generateResponse, sendReply } from "@/api/admin";
import { ApiError } from "@/api/client";
import { generateTestResponse, sendTestReply } from "@/api/test-tickets";
import { Spinner } from "@/components/Spinner";
import { useCurrentTabId } from "@/pages/admin/TabsContext";

// Must match backend/config.py:MAX_REPLY_CHARS. The shared SQL Server column
// GRS_GrievanceDetails.RepliedGrievance is NVARCHAR(500) – test tickets enforce
// the same limit so the UI behaves identically.
const MAX_REPLY_CHARS = 500;

export type AIResponseEditorMode = "real" | "test";

export function AIResponseEditor({
  mode,
  ticketId,
  applicationId,
  botResponse,
  finalReply,
  repliedBy,
  nextTicketPath,
  currentStatusId,
}: {
  mode: AIResponseEditorMode;
  ticketId: number;
  applicationId: string;
  botResponse: string | null;
  finalReply: string | null;
  repliedBy: string | null;
  // Real-ticket detail passes the URL of the next ticket in the current
  // filtered list (or null at the boundary). When null we fall back to the
  // returnPath so the user still ends up somewhere sensible.
  nextTicketPath?: string | null;
  // CurrentStatusID from GRS_GrievanceStatusDetails (real mode only). Send is
  // blocked until this is 1 (Mark-as-Viewed clicked). Ignored in test mode.
  currentStatusId?: number | null;
}) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const tabId = useCurrentTabId();
  const [draft, setDraft] = useState(botResponse ?? "");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const detailQueryKey =
    mode === "real"
      ? ["grievance", tabId, ticketId]
      : ["test-ticket", tabId, ticketId];
  const listQueryKey = mode === "real" ? ["grievances", tabId] : ["test-tickets", tabId];
  const historyQueryKey =
    mode === "real"
      ? ["history", tabId, ticketId]
      : ["test-ticket-history", tabId, ticketId];
  const returnPath = mode === "real" ? "/admin" : `/admin/tabs/${tabId}/test-tickets`;

  // Keep the draft in sync if the underlying detail changes (e.g. after Generate).
  useEffect(() => {
    setDraft(botResponse ?? "");
  }, [botResponse]);

  const generate = useMutation({
    mutationFn: () =>
      mode === "real"
        ? generateResponse(tabId, ticketId)
        : generateTestResponse(tabId, ticketId),
    onSuccess: (r) => {
      setDraft(r.bot_response);
      void qc.invalidateQueries({ queryKey: detailQueryKey });
      toast.success("AI response generated.");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed to generate response."),
  });

  const send = useMutation({
    mutationFn: () =>
      mode === "real"
        ? sendReply(tabId, ticketId, draft)
        : sendTestReply(tabId, ticketId, draft),
    onSuccess: (r) => {
      toast.success(`Reply sent (${r.replied_by}).`);
      void qc.invalidateQueries({ queryKey: detailQueryKey });
      void qc.invalidateQueries({ queryKey: listQueryKey });
      void qc.invalidateQueries({ queryKey: historyQueryKey });
      setConfirmOpen(false);
      // Real tickets jump straight to the next filtered ticket if there is
      // one; otherwise (or for test tickets) fall back to the returnPath.
      if (mode === "real" && nextTicketPath) {
        nav(nextTicketPath);
      } else {
        nav(returnPath);
      }
    },
    onError: (e) => {
      setConfirmOpen(false);
      toast.error(e instanceof ApiError ? e.message : "Failed to send reply.");
    },
  });

  // Mode 1: already acknowledged – read-only final reply.
  if (finalReply && finalReply.trim()) {
    return (
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Final Reply Sent to Student
        </h3>
        <div className="rounded-md border border-emerald-600/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-100 p-4 whitespace-pre-wrap text-sm leading-relaxed font-mono">
          {finalReply}
        </div>
        <div className="alert-success inline-flex items-center gap-2">
          <CheckCircle2 size={16} /> This ticket has been acknowledged and replied to
          {repliedBy ? ` (by: ${repliedBy})` : ""}.
        </div>
      </section>
    );
  }

  // Mode 2: pending, no draft yet.
  if (!botResponse || !botResponse.trim()) {
    return (
      <section className="space-y-4">
        <div className="alert-warning">
          {mode === "real"
            ? "The background service has not generated a response for this ticket yet."
            : "No AI draft yet. Click Generate to try one."}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            className="btn-secondary justify-center"
            onClick={() => qc.invalidateQueries({ queryKey: detailQueryKey })}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            className="btn-primary justify-center"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? (
              <Spinner label="Thinking…" />
            ) : (
              <>
                <Sparkles size={14} /> Generate AI Answer
              </>
            )}
          </button>
        </div>
      </section>
    );
  }

  // Mode 3: pending, has draft – editable + send.
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          AI Generated Response (Editable)
        </h3>
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="text-xs inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
        >
          <Sparkles size={12} />
          {generate.isPending ? "Regenerating…" : "Regenerate"}
        </button>
      </div>
      <textarea
        className="input-field min-h-[260px] font-mono text-sm leading-relaxed"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex items-center justify-between text-xs">
        <p className="text-slate-500 dark:text-slate-400">
          Your name is recorded as the responder. Sending an edited draft adds an
          <code> (edited)</code> marker.
        </p>
        <p
          className={
            draft.length > MAX_REPLY_CHARS
              ? "font-semibold text-red-600 dark:text-red-400"
              : draft.length > MAX_REPLY_CHARS * 0.9
                ? "text-amber-600 dark:text-amber-400"
                : "text-slate-500 dark:text-slate-400"
          }
        >
          {draft.length} / {MAX_REPLY_CHARS} characters
          {draft.length > MAX_REPLY_CHARS &&
            ` (over by ${draft.length - MAX_REPLY_CHARS})`}
        </p>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={
            send.isPending
            || !draft.trim()
            || draft.length > MAX_REPLY_CHARS
            || (mode === "real" && currentStatusId !== 1)
          }
          title={
            draft.length > MAX_REPLY_CHARS
              ? `Reply exceeds ${MAX_REPLY_CHARS}-character limit`
              : mode === "real" && currentStatusId == null
                ? "Cannot send – status record missing for this ticket."
                : mode === "real" && currentStatusId === 0
                  ? "Mark the ticket as viewed before sending."
                  : mode === "real" && currentStatusId === 4
                    ? "This ticket has already been replied to."
                    : undefined
          }
          onClick={() => setConfirmOpen(true)}
        >
          {send.isPending ? (
            <Spinner label="Sending…" />
          ) : (
            <>
              <Send size={14} /> Send to Student
            </>
          )}
        </button>
      </div>
      <ConfirmSendDialog
        open={confirmOpen}
        ticketId={ticketId}
        applicationId={applicationId}
        isSending={send.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => send.mutate()}
      />
    </section>
  );
}

function ConfirmSendDialog({
  open,
  ticketId,
  applicationId,
  isSending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  ticketId: number;
  applicationId: string;
  isSending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSending) onCancel();
      }}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white dark:bg-surface-raised border border-canvas-border dark:border-surface-border shadow-xl p-5 space-y-4">
          <DialogTitle className="text-base font-semibold">
            Send reply to student?
          </DialogTitle>
          <div className="text-sm text-slate-700 dark:text-slate-300 space-y-1">
            <p>Once sent, this reply cannot be edited or withdrawn.</p>
            <div className="mt-3 rounded-md border border-canvas-border dark:border-surface-border bg-slate-50 dark:bg-surface p-3 text-xs space-y-1">
              <div>
                <span className="text-slate-500 dark:text-slate-400">Ticket #</span>{" "}
                <span className="font-mono font-medium">{ticketId}</span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Application ID:</span>{" "}
                <span className="font-mono font-medium">{applicationId}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="btn-secondary text-xs px-3 py-1.5"
              onClick={onCancel}
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs px-3 py-1.5"
              onClick={onConfirm}
              disabled={isSending}
            >
              {isSending ? (
                <Spinner label="Sending…" />
              ) : (
                <>
                  <Send size={12} /> Send
                </>
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
